import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { assertSlotsConsistent } from './lib/slots';
import './styles/design.css';

// Dev-only sanity check — fails loud in console if pinned slot
// hashes drift from runtime keccak256 computation.
if (import.meta.env.DEV) {
  assertSlotsConsistent();
}

// BigInt-safe stringify so query keys with BigInt args (period ids, snapshot ids,
// addresses-as-bigint, etc.) hash deterministically. Without this, TanStack Query
// throws "Do not know how to serialize a BigInt" the moment any hook passes a
// bigint into the query key.
const bigIntHash = (key: readonly unknown[]): string =>
  JSON.stringify(key, (_, v) => (typeof v === 'bigint' ? v.toString() : v));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: bigIntHash,
      staleTime: 12_000,         // ~one Sepolia block
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root element missing in index.html');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>
);
